import React, { useState } from "react"
import pluralize from "pluralize"
import {
  Box, Flex, Grid, HStack, Stack, Text, chakra,
} from "@chakra-ui/react"
import { DioboxEvent, FormType } from "sharedTypes"
import { eventSubmissionsPath } from "utilities/routes"
import { asMoney } from "utilities/strings"
import { CHART_COLORS } from "utilities/constants"
import Icon from "components/elements/Icon"
import {
  BigStat, Legend, LegendItem, ProgressRows, Segment, StackedBar, TICKETING_COLOR,
} from "./charts"
import { EmptyState } from "./DashboardCard"
import ActivityChart, { ActivityMetric as Metric, PickedPeriod } from "./ActivityChart"
import SegmentedControl from "./SegmentedControl"
import DateRangeButton from "./DateRangeButton"
import {
  ActivityInterval, ActivityRange, defaultInterval, defaultRange, fitsHourly, rangeAnchor,
  snapRange, todayIn,
} from "../activitySeries"
import { EventPhase, eventEnd } from "../phase"

const INTERVALS: { value: ActivityInterval, label: string }[] = [
  { value: "hourly", label: "Hourly" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
]

const SHOWN_SESSIONS = 5

const METRIC_COLOR: Record<Metric, string> = {
  registrations: CHART_COLORS[0],
  ticketing: TICKETING_COLOR,
  checkins: "status.checkedIn",
}

const MetricTab = ({
  active, onClick, dot, label, value,
}: { active: boolean, onClick: () => void, dot: string, label: string, value: string }) => (
  <chakra.button
    type="button"
    onClick={onClick}
    display="flex"
    alignItems="center"
    gap={3}
    px={3.5}
    py={2}
    borderRadius="lg"
    textAlign="left"
    bg={active ? "white" : "transparent"}
    boxShadow={active ? "sm" : "none"}
    _hover={{ bg: active ? "white" : "whiteAlpha.700" }}
  >
    <Box boxSize={2} borderRadius="full" bg={dot} />
    <Box>
      <Text fontWeight="semibold" color="gray.700">{label}</Text>
      <Text fontSize="2xl" fontWeight="semibold" lineHeight="short" sx={{ fontVariantNumeric: "tabular-nums" }}>{value}</Text>
    </Box>
  </chakra.button>
)

const segments = (types: Partial<Record<string, number>>, eventId?: string): LegendItem[] => {
  const to = (formType: FormType) => eventId && `${eventSubmissionsPath(eventId)}?formType=${formType}`

  return [
    {
      label: "Registrations", value: types.registration ?? 0, color: CHART_COLORS[0], to: to(FormType.Registration),
    },
    {
      label: "RSVPs", value: types.rsvp ?? 0, color: CHART_COLORS[2], to: to(FormType.RSVP),
    },
    {
      label: "Tickets", value: types.ticket ?? 0, color: METRIC_COLOR.ticketing, to: to(FormType.Ticket),
    },
    {
      label: "Waitlists", value: types.waitlist ?? 0, color: CHART_COLORS[3], to: to(FormType.Waitlist),
    },
    {
      label: "Surveys", value: types.survey ?? 0, color: CHART_COLORS[5], to: to(FormType.Survey),
    },
  ]
}

const sum = (items: Segment[]) => items.reduce((total, { value }) => total + value, 0)

type Props = {
  event: DioboxEvent
  defaultMetric: Metric
  phase: EventPhase
}

const Activity = ({
  event, defaultMetric, phase,
}: Props) => {
  const [metric, setMetric] = useState<Metric>(defaultMetric)
  const [interval, setInterval] = useState<ActivityInterval>(defaultInterval(phase))
  const [picked, setPicked] = useState<PickedPeriod | null>(null)
  const [customRange, setCustomRange] = useState<ActivityRange | null>(null)
  const [allSessions, setAllSessions] = useState(false)
  const end = eventEnd(new Date(event.startDate), event.endDate, event.timezone)
  const anchor = rangeAnchor(phase, interval, new Date(), end)
  // A picked range stays as picked; Hourly shows up to a week, so only a longer one's last day.
  const narrowed = interval === "hourly" && customRange !== null && !fitsHourly(customRange)
  const shownCustom = narrowed && customRange
    ? { from: customRange.to, to: customRange.to }
    : customRange
  const range = shownCustom
    ? snapRange(interval, shownCustom)
    : defaultRange(interval, anchor, event.timezone)
  const today = todayIn(event.timezone)
  const { counts, currency } = event
  const lineItems = counts.formSubmissionLineItems as Record<string, number | undefined>
  const types = picked?.totals.registrations ?? lineItems
  const responseTypes = segments(types, event.id)
  // A hovered period lists the types the event has, at 0 when it had none of them.
  const eventTypes = segments(lineItems)
  const allResponses = sum(eventTypes)
  const responses = sum(responseTypes)
  const legendTypes = responseTypes.filter((_, i) => eventTypes[i].value > 0)
  const arrived = counts.arrivedGuests
  const sales = picked ? picked.totals : { gross: counts.grossSales, net: counts.netSales }
  const sessions = [...counts.checkInsByLocation ?? []]
    .sort((a, b) => Date.parse(a.firstAt) - Date.parse(b.firstAt))
  // Folding away a single session saves no space, so the list folds from two hidden on.
  const foldsSessions = sessions.length > SHOWN_SESSIONS + 1
  const shownSessions = foldsSessions && !allSessions ? sessions.slice(0, SHOWN_SESSIONS) : sessions
  // A period counts each guest at their first check-in; sessions may count them again.
  const checkInsSuffix = picked
    ? ` ${pluralize("first check-in", picked.totals.checkIns)}`
    : ` / ${counts.guests.toLocaleString()} checked in`
  // A hovered period shows its own session counts.
  const sessionRows = shownSessions.map((session) => ({
    ...session,
    guests: picked?.totals.sessions
      ? picked.totals.sessions[String(session.checkInLocationId)] ?? 0
      : session.guests,
  }))
  const statusBars = (
    <Stack
      spacing={0}
      visibility={picked ? "hidden" : undefined}
      aria-hidden={Boolean(picked)}
      data-testid="check-in-status"
    >
      <ProgressRows
        total={counts.guests}
        rows={[
          { label: "Checked In", value: arrived, color: "status.checkedIn" },
          {
            label: "Not Checked In",
            value: counts.guests - arrived,
            color: "status.notCheckedIn",
          },
        ]}
      />
    </Stack>
  )
  const sessionName = (id: number | null) => (
    event.checkInLocations.find((location) => location.id === id)?.name ?? "Other check-ins"
  )
  const pickMetric = (value: Metric) => { setMetric(value); setPicked(null) }
  const pickInterval = (value: ActivityInterval) => {
    setInterval(value)
    setPicked(null)
  }
  const pickRange = (value: ActivityRange) => {
    const shown = interval === "hourly" && !fitsHourly(value) ? "daily" : interval
    setInterval(shown)
    setCustomRange(value)
    setPicked(null)
  }

  if (!allResponses && !arrived && !Number(counts.netSales)) {
    return (
      <Box bg="white" borderWidth={1} borderColor="gray.300" borderRadius="md" p={6}>
        <EmptyState
          title="No activity yet"
          body="Registrations, ticket sales and check-ins will be charted here as they come in."
        />
      </Box>
    )
  }

  return (
    <Box as="section" bg="white" borderWidth={1} borderColor="gray.300" borderRadius="md">
      <Flex
        px={5}
        py={4}
        borderBottomWidth={1}
        borderColor="gray.300"
        justify="space-between"
        align="flex-start"
        gap={4}
        wrap="wrap"
      >
        <HStack
          spacing={1.5}
          bg="gray.100"
          p={1}
          borderRadius="xl"
          display="inline-flex"
          flexWrap="wrap"
        >
          <MetricTab
            active={metric === "registrations"}
            onClick={() => pickMetric("registrations")}
            dot={METRIC_COLOR.registrations}
            label="Responses"
            value={allResponses.toLocaleString()}
          />
          <MetricTab
            active={metric === "ticketing"}
            onClick={() => pickMetric("ticketing")}
            dot={METRIC_COLOR.ticketing}
            label="Ticketing"
            value={asMoney(counts.netSales, currency, true)}
          />
          <MetricTab
            active={metric === "checkins"}
            onClick={() => pickMetric("checkins")}
            dot={METRIC_COLOR.checkins}
            label="Check-ins"
            value={arrived.toLocaleString()}
          />
        </HStack>
        <Flex flex="1 1 0" justify="flex-end" wrap="wrap" gap={2}>
          <SegmentedControl value={interval} onChange={pickInterval} options={INTERVALS} />
          <DateRangeButton
            range={range}
            picked={shownCustom ?? undefined}
            onApply={pickRange}
            today={today}
          />
        </Flex>
      </Flex>
      <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }}>
        <Box p={4} borderRightWidth={{ lg: 1 }} borderColor="gray.300" minW={0}>
          <ActivityChart
            eventId={event.id}
            timezone={event.timezone}
            currency={currency}
            metric={metric}
            interval={interval}
            color={METRIC_COLOR[metric]}
            range={range}
            onPick={setPicked}
          />
        </Box>
        <Box p={6}>
          {metric === "registrations" && (
            <Stack spacing={6}>
              <Stack spacing={0.5}>
                <Text fontWeight="semibold" color="gray.600">
                  {picked?.label ?? "All responses"}
                </Text>
                <Text fontSize="2xl" fontWeight="semibold">
                  {responses.toLocaleString()}{" "}
                  <Text as="span" fontSize="sm" fontWeight="normal" color="gray.600">
                    responses
                  </Text>
                </Text>
              </Stack>
              <Stack spacing={4}>
                <StackedBar items={responseTypes} />
                <Legend items={legendTypes} total={responses} />
              </Stack>
            </Stack>
          )}
          {metric === "ticketing" && (
            <Stack spacing={6}>
              <Stack spacing={4}>
                <Text fontWeight="semibold" color="gray.600">
                  {picked?.label ?? "All sales"}
                </Text>
                <BigStat label="Net sales" value={asMoney(sales.net, currency)} />
                <BigStat label="Gross sales" value={asMoney(sales.gross, currency)} />
              </Stack>
              <Stack spacing={2}>
                <Flex justify="space-between">
                  <Text color="gray.600" fontWeight="semibold">Tickets sold</Text>
                  <Text fontWeight="semibold">{(types.ticket ?? 0).toLocaleString()}</Text>
                </Flex>
                <Flex justify="space-between">
                  <Text color="gray.600" fontWeight="semibold">Fees and refunds</Text>
                  <Text fontWeight="semibold">
                    {asMoney(sales.gross - sales.net, currency)}
                  </Text>
                </Flex>
              </Stack>
            </Stack>
          )}
          {metric === "checkins" && (
            <Stack spacing={6}>
              <Stack spacing={0.5}>
                <Text fontWeight="semibold" color="gray.600">{picked?.label ?? "All guests"}</Text>
                <Text fontSize="2xl" fontWeight="semibold">
                  {(picked ? picked.totals.checkIns : arrived).toLocaleString()}
                  <Text as="span" fontSize="sm" fontWeight="normal" color="gray.600">
                    {checkInsSuffix}
                  </Text>
                </Text>
              </Stack>
              {!picked && statusBars}
              {sessions.some(({ checkInLocationId }) => checkInLocationId !== null)
                && (!picked || picked.totals.sessions) && (
                <Stack spacing={2.5}>
                  <Text fontWeight="semibold" color="gray.600">By session</Text>
                  <Grid templateColumns="minmax(0, 1fr) auto auto" columnGap={1} rowGap={2.5}>
                    {sessionRows.map(({
                      checkInLocationId, guests, firstAt,
                    }) => (
                      <Box key={`${checkInLocationId}-${firstAt}`} display="contents" data-testid="check-in-session">
                        <Text fontWeight="semibold" noOfLines={1} mr={2}>{sessionName(checkInLocationId)}</Text>
                        <Text fontWeight="semibold" textAlign="right">{guests.toLocaleString()}</Text>
                        <Text color="gray.600">{pluralize("guest", guests)}</Text>
                      </Box>
                    ))}
                  </Grid>
                  {foldsSessions && (
                    <chakra.button
                      type="button"
                      onClick={() => setAllSessions((open) => !open)}
                      alignSelf="flex-start"
                      display="inline-flex"
                      alignItems="center"
                      gap={1.5}
                      fontWeight="semibold"
                      color="blue.600"
                      _hover={{ color: "blue.700" }}
                    >
                      {allSessions
                        ? "Show fewer"
                        : `View ${sessions.length - SHOWN_SESSIONS} more sessions`}
                      {!allSessions && <Icon icon="next-arrow" size={2.5} ml={-0.5} />}
                    </chakra.button>
                  )}
                </Stack>
              )}
              {picked && statusBars}
            </Stack>
          )}
        </Box>
      </Grid>
    </Box>
  )
}

export default Activity
